import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Sparkles } from 'lucide-angular';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ReductionActionsService } from 'src/app/shared/services/reduction-actions.service';
import {
  ReductionActionCode,
  ReductionActionDef,
} from 'src/app/shared/model/reduction-action';

/**
 * Reduction actions the company commits to — these are reduction tips, and the
 * selected ones are highlighted on every trip report. Kept separate from custom
 * actions and SDGs, which are declarative only.
 */
@Component({
  selector: 'app-reduction-actions',
  standalone: true,
  imports: [FormsModule, TranslocoModule, CheckboxModule, LucideAngularModule],
  templateUrl: './reduction-actions.component.html',
})
export class ReductionActionsComponent implements OnInit {
  private reductionActionsService = inject(ReductionActionsService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly Sparkles = Sparkles;

  catalogue = signal<ReductionActionDef[]>([]);
  isSaving = signal(false);

  selectedActions = this.reductionActionsService.reductionActions;
  selectedCount = computed(() => this.selectedActions().length);

  ngOnInit(): void {
    this.reductionActionsService.getCatalogue().subscribe({
      next: (catalogue) => this.catalogue.set(catalogue),
      error: () => this.catalogue.set([]),
    });
  }

  isSelected(code: ReductionActionCode): boolean {
    return this.selectedActions().includes(code);
  }

  toggle(code: ReductionActionCode): void {
    if (this.isSaving()) return;

    const current = this.selectedActions();
    const next = current.includes(code)
      ? current.filter((c) => c !== code)
      : [...current, code];

    this.isSaving.set(true);
    this.reductionActionsService.updateReductionActions(next).subscribe({
      next: () => this.isSaving.set(false),
      error: () => {
        this.isSaving.set(false);
        this.notificationService.error(
          this.translocoService.translate('objectives.sdgSaveError'),
        );
      },
    });
  }
}
